import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical } from "lucide-react"
import type { Registry } from "@/api.js"
import { Card } from "@/components/ui/card.js"
import { Input } from "@/components/ui/input.js"
import { cn } from "@/lib/utils.js"
import { type ConditionVocabulary, describeConditions, vocabularyOf } from "./condition-text.js"
import { groupBySource, sourceKey } from "./groups.js"
import { assignPriorities, type EditorRule, laneTitle } from "./model.js"

/**
 * Every rule in the set, as a list a person scans and picks from.
 *
 * **One line per rule, and the line says what the rule does to a decision.**
 * The grid this replaces put six controls on every row, so fourteen rules
 * were fourteen identical rows of selects and sliders and the eye had nothing
 * to land on; the product owner's word for it was *zlewają się*. Here a row is
 * its order, its name and its effect — *Weto*, *20 pkt*, *0 pkt*, *Wył.* — and
 * everything that sets a rule lives in the editor beside the list, where it has
 * room. The rows are grouped by source, under the source's own name, because
 * that is how somebody looks for a rule: "the KRZ ones".
 *
 * Dragging reorders within a source. A rule cannot be dragged out of costing
 * what its source costs, and the order between two sources in one cost lane
 * decides nothing a person would reach for — see `groupBySource`.
 */
export function RuleList({
  country,
  rules,
  registry,
  threshold,
  filter,
  onFilter,
  selected,
  onSelect,
  onReorder,
  changed,
}: {
  country: string
  /** The whole set, enabled and not. The list groups them. */
  rules: readonly EditorRule[]
  registry: Registry
  threshold: number
  filter: string
  onFilter: (next: string) => void
  selected: string | null
  onSelect: (ruleId: string) => void
  onReorder: (order: readonly EditorRule[]) => void
  /** Rules the draft has edited since it was seeded — marked so they can be found again. */
  changed: ReadonlySet<string>
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const vocabulary = vocabularyOf(registry)

  const running = rules.filter((rule) => rule.enabled)
  const priorities = assignPriorities(running, registry)
  const off = rules.filter((rule) => !rule.enabled)
  const groups = groupBySource(running, registry)

  const needle = filter.trim().toLowerCase()
  const matches = (rule: EditorRule) =>
    needle === "" ||
    [rule.title, rule.ruleId, ...rule.dependsOn].some((text) => text.toLowerCase().includes(needle))

  function onDragEnd(event: DragEndEvent) {
    const over = event.over
    if (over === null || over.id === event.active.id) return
    const from = running.findIndex((rule) => rule.ruleId === event.active.id)
    const to = running.findIndex((rule) => rule.ruleId === over.id)
    if (from === -1 || to === -1) return

    const moved = running[from]
    const target = running[to]
    if (moved === undefined || target === undefined) return
    if (sourceKey(moved) !== sourceKey(target)) return

    onReorder(arrayMove([...running], from, to))
  }

  const item = (rule: EditorRule, priority: number | null, draggable: boolean) => (
    <RuleItem
      key={rule.ruleId}
      vocabulary={vocabulary}
      rule={rule}
      priority={priority}
      threshold={threshold}
      draggable={draggable}
      selected={selected === rule.ruleId}
      changed={changed.has(rule.ruleId)}
      onSelect={() => onSelect(rule.ruleId)}
    />
  )

  return (
    <Card className="overflow-hidden">
      {/* Past six rules a person looks for one; below that they can see them all. */}
      {rules.length > 6 && (
        <div className="border-b px-3 py-2.5">
          <Input
            placeholder="Filtruj reguły…"
            aria-label="Filtruj reguły"
            value={filter}
            onChange={(event) => onFilter(event.target.value)}
          />
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div className="py-1">
          {groups.map((group) => {
            const shown = group.rules.filter(matches)
            if (shown.length === 0) return null

            return (
              // A source can head two groups if the order interleaves it, so
              // the key is the first rule's, which appears once.
              <section key={group.rules[0]?.ruleId ?? group.key} aria-label={group.title}>
                <GroupHeading
                  title={group.title}
                  count={group.rules.length}
                  hint={laneTitle(group.lane).toLowerCase()}
                />
                <SortableContext
                  items={shown.map((rule) => rule.ruleId)}
                  strategy={verticalListSortingStrategy}
                >
                  <ol>
                    {shown.map((rule) =>
                      // Ordering means nothing while the list is filtered, and a
                      // group of one has no order to set.
                      item(
                        rule,
                        priorities[running.indexOf(rule)] ?? null,
                        needle === "" && group.rules.length > 1,
                      ),
                    )}
                  </ol>
                </SortableContext>
              </section>
            )
          })}

          {off.length > 0 && off.some(matches) && (
            <section aria-label="Wyłączone">
              <GroupHeading title="Wyłączone" count={off.length} hint="nie są pytane" />
              <ol>{off.filter(matches).map((rule) => item(rule, null, false))}</ol>
            </section>
          )}
        </div>
      </DndContext>

      {/*
       * Two empty states, and telling them apart is the point: a set with
       * nothing switched on is somebody's decision, a set nothing can serve is a
       * country we do not cover. Both answer prepayment; only one is a mistake.
       */}
      {rules.length === 0 && (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">
          Żadne zainstalowane źródło nie obsługuje <span className="font-mono">{country}</span> —
          ten zestaw zawsze odpowie przedpłatą.
        </p>
      )}
      {rules.length > 0 && running.length === 0 && (
        <p className="border-t px-4 py-3 text-[13px] text-critical">
          Żadna reguła nie jest włączona — odpowiedzią zostaje przedpłata z{" "}
          <span className="font-mono text-[12.5px]">core.no_applicable_rule</span>.
        </p>
      )}
      {rules.length > 0 && needle !== "" && !rules.some(matches) && (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">Nic nie pasuje do filtra.</p>
      )}
    </Card>
  )
}

/** A source, as a heading over its rules: its name, how many, and what asking it costs. */
function GroupHeading({ title, count, hint }: { title: string; count: number; hint: string }) {
  return (
    <div className="flex items-baseline gap-x-2 px-3 pt-3 pb-1">
      <h4 className="min-w-0 truncate text-[12.5px] font-semibold text-foreground">{title}</h4>
      <span className="font-mono text-[12px] text-muted-foreground">{count}</span>
      <span className="ml-auto shrink-0 text-[12px] text-muted-foreground">{hint}</span>
    </div>
  )
}

function RuleItem({
  rule,
  vocabulary,
  priority,
  threshold,
  draggable,
  selected,
  changed,
  onSelect,
}: {
  rule: EditorRule
  /** What a condition is rendered from. Served, never hardcoded here. */
  vocabulary: ConditionVocabulary
  priority: number | null
  threshold: number
  draggable: boolean
  selected: boolean
  changed: boolean
  onSelect: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.ruleId,
    disabled: !draggable,
  })

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-stretch border-l-2 transition-colors",
        selected ? "border-l-primary bg-accent" : "border-l-transparent hover:bg-muted/40",
        isDragging && "relative z-10 bg-card shadow-lg",
      )}
    >
      {draggable ? (
        <button
          type="button"
          className="cursor-grab px-1.5 text-muted-foreground/40 hover:text-muted-foreground active:cursor-grabbing"
          aria-label={`Przeciągnij ${rule.title}`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-3.5" />
        </button>
      ) : (
        <span className="w-[26px] shrink-0" />
      )}

      {/*
       * The row is the control: it selects. The identifier a ticket quotes is
       * in the editor, and the filter matches it, so the row keeps to what a
       * scan needs — where it runs, what it is called, what it does.
       */}
      <button
        type="button"
        aria-current={selected ? "true" : undefined}
        className="flex min-w-0 flex-1 items-center gap-2.5 py-[7px] pr-3 text-left"
        onClick={onSelect}
      >
        <span className="w-7 shrink-0 font-mono text-[12px] tabular-nums text-muted-foreground">
          {priority === null ? "—" : priority}
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 truncate text-[13.5px]",
            rule.enabled ? "text-foreground" : "text-muted-foreground",
            selected && "font-medium",
          )}
        >
          {rule.title}
        </span>
        {changed && (
          <span
            role="img"
            aria-label="Niezapisana zmiana"
            title="Niezapisana zmiana"
            className="size-1.5 shrink-0 rounded-full bg-primary"
          />
        )}
        {rule.conditions.length > 0 && (
          /*
           * **A conditioned rule says so where the list is scanned.** What it
           * does to a decision is only half the answer once a rule can decline
           * to run at all, and for a metered source the other half is whether
           * money is spent. The sentence itself does not fit a row, so the row
           * carries the fact and the tooltip the terms; the editor carries the
           * control.
           */
          <span
            className="shrink-0 text-[12px] text-muted-foreground"
            title={`Tylko gdy: ${describeConditions(rule.conditions, vocabulary)}`}
          >
            warunkowa
          </span>
        )}
        <Effect rule={rule} threshold={threshold} />
      </button>
    </li>
  )
}

/**
 * What the rule does to a decision when it fires, in one or two words. The one
 * fact worth a column in a list that has no columns: a veto wears the risk
 * colour, a weight that reaches the threshold does too, a weight of zero and a
 * rule switched off are quiet.
 */
function Effect({ rule, threshold }: { rule: EditorRule; threshold: number }) {
  if (!rule.enabled) {
    return <span className="shrink-0 text-[12px] text-muted-foreground">Wył.</span>
  }
  if (rule.mode === "veto") {
    return <span className="shrink-0 text-[12px] font-medium text-critical">Weto</span>
  }
  const decisive = rule.weight >= threshold
  return (
    <span
      className={cn(
        "shrink-0 text-[12px] tabular-nums",
        decisive
          ? "font-medium text-critical"
          : rule.weight > 0
            ? "font-medium text-foreground"
            : "text-muted-foreground",
      )}
      title={decisive ? "Ta waga blokuje sama" : undefined}
    >
      {rule.weight} pkt
    </span>
  )
}
